"use client"

import { Bell, Search, Menu } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAdminAuth } from "@/lib/admin-auth-context"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { useNavigate, useLocation } from "react-router-dom"
import { useState } from "react"

const pageTitles: Record<string, { title: string; description: string }> = {
  "/admin": { title: "Dashboard", description: "Sayt statistikasi va oxirgi faoliyat" },
  "/admin/products": { title: "Mahsulotlar", description: "Mahsulotlarni boshqarish" },
  "/admin/categories": { title: "Kategoriyalar", description: "Kategoriyalarni boshqarish" },
  "/admin/orders": { title: "Buyurtmalar", description: "Buyurtmalarni boshqarish" },
  "/admin/translations": { title: "Tillar", description: "Ko'p tilli tarjimalarni boshqarish" },
  "/admin/telegram": { title: "Telegram Bot", description: "Telegram bot sozlamalari" },
  "/admin/settings": { title: "Sozlamalar", description: "Sayt sozlamalari" },
}

export function AdminHeader() {
  const { user, logout } = useAdminAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [notificationCount] = useState(3)

  const pageInfo = pageTitles[pathname] || { title: "Admin Panel", description: "" }

  return (
    <header className="sticky top-0 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-slate-200 dark:border-gray-800">
      <div className="h-20 px-4 lg:px-8 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" className="lg:hidden">
            <Menu className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">{pageInfo.title}</h1>
            {pageInfo.description && (
              <p className="text-sm text-muted-foreground hidden sm:block">{pageInfo.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 lg:gap-4">
          {/* Search */}
          <div className="relative hidden lg:block">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Qidirish..."
              className="w-72 pl-11 bg-slate-100 dark:bg-gray-800 border-0 focus-visible:ring-emerald-500"
            />
          </div>

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative hover:bg-slate-100 dark:hover:bg-gray-800">
                <Bell className="h-5 w-5" />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {notificationCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>Bildirishnomalar</span>
                <span className="text-xs text-muted-foreground">{notificationCount} ta yangi</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="max-h-64 overflow-y-auto">
                <DropdownMenuItem className="flex flex-col items-start gap-1 p-3 cursor-pointer">
                  <p className="font-medium text-sm">Yangi buyurtma</p>
                  <p className="text-xs text-muted-foreground">Alisher Karimov - 150,000 so'm</p>
                  <p className="text-xs text-emerald-600">2 daqiqa oldin</p>
                </DropdownMenuItem>
                <DropdownMenuItem className="flex flex-col items-start gap-1 p-3 cursor-pointer">
                  <p className="font-medium text-sm">Mahsulot tugayapti</p>
                  <p className="text-xs text-muted-foreground">Asal 500g - faqat 3 ta qoldi</p>
                  <p className="text-xs text-amber-600">15 daqiqa oldin</p>
                </DropdownMenuItem>
                <DropdownMenuItem className="flex flex-col items-start gap-1 p-3 cursor-pointer">
                  <p className="font-medium text-sm">Yangi buyurtma</p>
                  <p className="text-xs text-muted-foreground">Madina Rahimova - 85,000 so'm</p>
                  <p className="text-xs text-emerald-600">1 soat oldin</p>
                </DropdownMenuItem>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-center text-emerald-600 hover:text-emerald-700 cursor-pointer justify-center font-medium"
                onClick={() => navigate("/admin/orders")}
              >
                Barchasini ko'rish
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-3 hover:bg-slate-100 dark:hover:bg-gray-800 h-12 px-3">
                <Avatar className="h-9 w-9 border-2 border-emerald-500">
                  <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-semibold">
                    {user?.username?.charAt(0).toUpperCase() || "A"}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left">
                  <p className="font-semibold text-sm">{user?.username || "Admin"}</p>
                  <p className="text-xs text-muted-foreground">Administrator</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Mening hisobim</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/admin/settings")} className="cursor-pointer">
                Profil sozlamalari
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/admin/settings")} className="cursor-pointer">
                Parolni o'zgartirish
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={logout}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 cursor-pointer"
              >
                Chiqish
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}
